// Core
import React, {
  useCallback,
  useState,
} from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Hooks
import { useBreakpoint } from '../../../../hooks/useBreakpoint';

// Plugins
import {
  formatCrusherModel,
  isEnabled,
  lastOperationalUnitDataCone,
  lastOperationalEdgeStatus,
} from '../../../../plugins/crusher';

// Components
import { Alert, AlertType } from '../../../../layout/elements/Alert';
import { AlertLine } from '../../../../layout/icons';
import { BreadcrumbItem } from '../../../../layout/elements/Breadcrumb';
import { Col } from '../../../../layout/elements/Col';
import { Row } from '../../../../layout/elements/Row';

// Components
import {
  CameraFeed,
  LiveStatus,
} from '../../../../components/CameraFeed';

// Crusher Modules
import { Camera } from './Modules/Camera';
import { CavityLevel } from './Modules/CavityLevel';
import { CSSettings } from './Modules/CSSettings';
import { Details } from './Modules/Details';
import { FeederInterlock as FeederInterlockModule } from './Modules/FeederInterlock';
import { Hydraulic } from './Modules/Hydraulic';
import { Indicators } from './Modules/Indicators';
import { LinerWear } from './Modules/LinerWear';
import { Lubrication } from './Modules/Lubrication';
import { MotorControl as MotorControlModule } from './Modules/MotorControl';
import { MotorStatus as MotorStatusModule } from './Modules/MotorStatus';
import { Vibration } from './Modules/Vibration';

// HMI Alarms
import { HMIAlarms } from './HMIAlarms';

// Types
import {
  CrusherDrawerType,
  EdgeDeviceOperationalStatus,
  EdgeDeviceOperationalStatusStatus,
} from '../../../../types/global';
import {
  EquipmentUnitDataConeCrusher,
  TrendingType,
} from '../../../../types/crusher';

// Drawers
import { Export } from './Drawers/Export';
import { FeederInterlock } from './Drawers/FeederInterlock';
import { HydraulicUnit } from './Drawers/HydraulicUnit';
import { LubricationUnit } from './Drawers/LubricationUnit';
import { MotorControl } from './Drawers/MotorControl';
import { MotorStatus } from './Drawers/MotorStatus';

// Maintenance
import { Maintenance } from './Maintenance/Maintenance';
import { MaintenanceType } from './Maintenance';

// Trendings
import { Trendings } from './Trendings';

// View
import {
  AlertContainer,
  CavityLevelContainer,
  CrusherMonitoringContainer,
  CSSettingsAndLinerWearContainer,
  LeftMetricsContainer,
  RightMetricsContainer,
} from './Patriot.style';
import {
  PatriotProps,
  PatriotDrawerType,
} from './Patriot.types';

function Patriot({
  alertText,
  equipmentUnitCrusher,
  operationalData,
  site,
  unitCoordinates,
  testId,
}: PatriotProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();

  // Responsive conditions
  const {
    isXs,
    isSm,
    isMd,
    isLg,
  } = useBreakpoint();
  const isMobileOrTablet: boolean = isXs || isSm || isMd || isLg;

  /* ***********************************************************************************************
  **************************************** INITIAL STATE *******************************************
  *********************************************************************************************** */

  type State = {
    drawers: {
      isOpen: boolean;
      selectedDrawer: PatriotDrawerType;
    };
    trendings: {
      isOpen: boolean;
      selectedTrending: TrendingType;
    };
    maintenance: {
      isOpen: boolean;
      selectedView: MaintenanceType;
    };
    cameraModal: {
      isOpen: boolean;
    };
  };

  const initialState: State = {
    drawers: {
      isOpen: false,
      selectedDrawer: PatriotDrawerType.hydraulic,
    },
    trendings: {
      isOpen: false,
      selectedTrending: TrendingType.motorCurrent,
    },
    maintenance: {
      isOpen: false,
      selectedView: MaintenanceType.maintenance,
    },
    cameraModal: {
      isOpen: false,
    },
  };

  const [state, setState] = useState<State>(initialState);

  /* ***********************************************************************************************
  ************************************** COMPONENT HANDLING ****************************************
  *********************************************************************************************** */

  const lastUnitData: EquipmentUnitDataConeCrusher | null = lastOperationalUnitDataCone(
    operationalData,
  );
  const lastEdgeStatus: EdgeDeviceOperationalStatus | null = lastOperationalEdgeStatus(
    operationalData,
  );

  // Check if at least one vibration sensor is enabled
  const hasVibrationSensorEnabled: boolean = lastUnitData
    ? (
      isEnabled(lastUnitData.vibrationSensor1Enable)
      || isEnabled(lastUnitData.vibrationSensor2Enable)
      || isEnabled(lastUnitData.vibrationSensor3Enable)
      || isEnabled(lastUnitData.vibrationSensor4Enable)
    )
    : false;

  const handleMaintenanceViewChange = useCallback((view: MaintenanceType): void => {
    setState((currentState: State): State => ({
      ...currentState,
      maintenance: {
        ...currentState.maintenance,
        selectedView: view,
      },
    }));
  }, []);

  // Breadcrumb items
  const breadcrumbItems: BreadcrumbItem[] = [
    { text: t('common.plant'), target: '/plant-management' },
    {
      text: equipmentUnitCrusher.customerUnitName || formatCrusherModel(equipmentUnitCrusher.model),
    },
  ];

  /**
   * Renders the appropriate drawer component based on the provided drawer type.
   *
   * @param {CrusherDrawer} drawer - The type of drawer to render.
   * @returns {JSX.Element | null} The drawer component if the drawer type is valid, otherwise null.
   */
  const renderCrusherDrawer = (drawer: PatriotDrawerType): JSX.Element | null => {
    switch (drawer) {
      case PatriotDrawerType.feederInterlock:
        return (
          <FeederInterlock
            onCloseDrawer={(): void => setState({
              ...state,
              drawers: { ...state.drawers, isOpen: false },
            })}
            isDrawerOpen={state.drawers.isOpen}
            unitData={lastUnitData}
            edgeStatus={lastEdgeStatus}
            testId="feeder-interlock-drawer"
          />
        );
      case PatriotDrawerType.hmiAlarms:
        return state.drawers.isOpen && state.drawers.selectedDrawer ? (
          <HMIAlarms
            onCloseDrawer={(): void => setState({
              ...state,
              drawers: { ...state.drawers, isOpen: false },
            })}
            isDrawerOpen={state.drawers.isOpen}
            unitData={lastUnitData}
          />
        ) : null;
      case PatriotDrawerType.hydraulic:
        return (
          <HydraulicUnit
            onCloseDrawer={(): void => setState({
              ...state,
              drawers: { ...state.drawers, isOpen: false },
            })}
            isDrawerOpen={state.drawers.isOpen}
            unitData={lastUnitData}
            edgeStatus={lastEdgeStatus}
            testId="hydraulic-unit-drawer"
          />
        );
      case PatriotDrawerType.lubrication:
        return (
          <LubricationUnit
            onCloseDrawer={(): void => setState({
              ...state,
              drawers: { ...state.drawers, isOpen: false },
            })}
            isDrawerOpen={state.drawers.isOpen}
            unitData={lastUnitData}
            edgeStatus={lastEdgeStatus}
            testId="lubrication-unit-drawer"
          />
        );
      case PatriotDrawerType.motorControl:
        return (
          <MotorControl
            onCloseDrawer={(): void => setState({
              ...state,
              drawers: { ...state.drawers, isOpen: false },
            })}
            isDrawerOpen={state.drawers.isOpen}
            unitData={lastUnitData}
            edgeStatus={lastEdgeStatus}
            testId="motor-control-drawer"
          />
        );
      case PatriotDrawerType.motorStatus:
        return (
          <MotorStatus
            onCloseDrawer={(): void => setState({
              ...state,
              drawers: { ...state.drawers, isOpen: false },
            })}
            isDrawerOpen={state.drawers.isOpen}
            unitData={lastUnitData}
            onOpenCoastDownHistory={(): void => setState({
              ...state,
              trendings: {
                ...state.trendings,
                isOpen: true,
                selectedTrending: TrendingType.coastDown,
              },
            })}
            edgeStatus={lastEdgeStatus}
            testId="motor-status-drawer"
          />
        );
      case PatriotDrawerType.export:
        return (
          <Export
            onCloseDrawer={(): void => setState({
              ...state,
              drawers: { ...state.drawers, isOpen: false },
            })}
            isDrawerOpen={state.drawers.isOpen}
            equipmentUnit={equipmentUnitCrusher}
          />
        );
      default:
        return null;
    }
  };

  return (
    <CrusherMonitoringContainer data-testid={testId}>

      {alertText && (
        <Row>
          <Col>
            <AlertContainer>
              <Alert
                icon={<AlertLine />}
                testId="alert-status"
                title={alertText}
                className="drawer-header-alert"
                closable={false}
                type={AlertType.warning}
              />
            </AlertContainer>
          </Col>
        </Row>
      )}
      <Row
        style={{ flex: 1, rowGap: 8 }}
        gutter={{
          xs: 0,
          lg: 0,
          sm: 0,
          md: 0,
          xl: 4,
          xxl: 4,
        }}
      >
        <Col xl={5} lg={24} md={24} sm={24} xs={24}>
          <LeftMetricsContainer>
            <Details
              breadcrumbItems={breadcrumbItems}
              unitData={lastUnitData}
              edgeStatus={lastEdgeStatus}
              equipmentUnitCrusher={equipmentUnitCrusher}
              testId="details-module"
              onDrawerOpen={(drawerType: CrusherDrawerType): void => {
                switch (drawerType) {
                  case CrusherDrawerType.maintenance:
                    setState({
                      ...state,
                      maintenance: {
                        ...state.maintenance,
                        isOpen: true,
                      },
                    });
                    break;
                  case CrusherDrawerType.trending:
                    setState({
                      ...state,
                      trendings: {
                        ...state.trendings,
                        isOpen: true,
                      },
                    });
                    break;
                  case CrusherDrawerType.alarms:
                    setState({
                      ...state,
                      drawers: {
                        ...state.drawers,
                        isOpen: true,
                        selectedDrawer: PatriotDrawerType.hmiAlarms,
                      },
                    });
                    break;
                  case CrusherDrawerType.export:
                    setState({
                      ...state,
                      drawers: {
                        ...state.drawers,
                        isOpen: true,
                        selectedDrawer: PatriotDrawerType.export,
                      },
                    });
                    break;
                  default:
                    break;
                }
              }}
              onModalOpen={(): void => setState((currentState: State): State => ({
                ...currentState,
                cameraModal: { isOpen: true },
              }))}
            />
            {isMobileOrTablet && (
              <Indicators
                testId="indicators-module-mobile"
                unitData={lastUnitData}
                edgeStatus={lastEdgeStatus}
              />
            )}
            <CSSettingsAndLinerWearContainer data-testid="cs-settings-liner-wear-container">
              <CSSettings
                testId="cs-settings-module"
                unitData={lastUnitData}
              />

              {((isMd || isLg) && lastUnitData?.linerWearEnable) && (
                <LinerWear
                  testId="liner-wear-module-md"
                  unitData={lastUnitData}
                  decimalPlaces={2}
                />
              )}
            </CSSettingsAndLinerWearContainer>
            {((isSm || isXs) && lastUnitData?.linerWearEnable) && (
              <LinerWear
                testId="liner-wear-module-mobile"
                unitData={lastUnitData}
                decimalPlaces={2}
              />
            )}
            <FeederInterlockModule
              testId="feeder-automation-module"
              onOpenDrawer={(): void => {
                setState({
                  ...state,
                  drawers: {
                    ...state.drawers,
                    isOpen: true,
                    selectedDrawer: PatriotDrawerType.feederInterlock,
                  },
                });
              }}
            />
            <MotorControlModule
              testId="motor-control-module"
              onOpenDrawer={(): void => {
                setState({
                  ...state,
                  drawers: {
                    ...state.drawers,
                    isOpen: true,
                    selectedDrawer: PatriotDrawerType.motorControl,
                  },
                });
              }}
            />
            {hasVibrationSensorEnabled && !isMobileOrTablet && (
              <Vibration
                testId="vibration-module"
                unitData={lastUnitData}
                edgeStatus={lastEdgeStatus}
                onOpenDrawer={(): void => {
                  setState({
                    ...state,
                    drawers: {
                      ...state.drawers,
                      isOpen: true,
                      selectedDrawer: PatriotDrawerType.motorStatus,
                    },
                  });
                }}
              />
            )}
          </LeftMetricsContainer>
        </Col>

        <Col xl={19} lg={24} md={24} sm={24} xs={24}>
          <RightMetricsContainer>
            {/* Indicators */}
            {!isMobileOrTablet && (
              <Row gutter={8} style={{ rowGap: 8 }}>
                <Col cols={lastUnitData?.linerWearEnable ? 20 : 24}>
                  <Indicators
                    testId="indicators-module"
                    unitData={lastUnitData}
                    edgeStatus={lastEdgeStatus}
                  />
                </Col>
                {lastUnitData?.linerWearEnable && (
                  <Col lg={4} xs={0}>
                    <LinerWear
                      testId="liner-wear-module-desktop"
                      unitData={lastUnitData}
                      decimalPlaces={2}
                    />
                  </Col>
                )}
              </Row>
            )}

            {/* Hydraulic and Motor Status */}
            <Row
              gutter={{
                xs: 0,
                lg: 0,
                sm: 0,
                md: 0,
                xl: 8,
                xxl: 8,
              }}
              style={{ flex: 1, rowGap: 8 }}
            >
              <Col xl={lastUnitData?.cavityLevelSensorEnable ? 10 : 12} xs={24}>
                <Hydraulic
                  unitData={lastUnitData}
                  edgeStatus={lastEdgeStatus}
                  testId="hydraulic-module"
                  onOpenDrawer={(): void => {
                    setState({
                      ...state,
                      drawers: {
                        ...state.drawers,
                        isOpen: true,
                        selectedDrawer: PatriotDrawerType.hydraulic,
                      },
                    });
                  }}
                />
              </Col>
              <Col
                xl={lastUnitData?.cavityLevelSensorEnable ? 10 : 12}
                xs={24}
                lg={lastUnitData?.cavityLevelSensorEnable ? 16 : 24}
                md={lastUnitData?.cavityLevelSensorEnable ? 16 : 24}
              >
                <MotorStatusModule
                  testId="motor-status-module"
                  unitData={lastUnitData}
                  edgeStatus={lastEdgeStatus}
                  onOpenDrawer={(): void => {
                    setState({
                      ...state,
                      drawers: {
                        ...state.drawers,
                        isOpen: true,
                        selectedDrawer: PatriotDrawerType.motorStatus,
                      },
                    });
                  }}
                />
              </Col>
              {lastUnitData?.cavityLevelSensorEnable && (
                <Col xl={4} md={8} sm={24} xs={24}>
                  <CavityLevelContainer>
                    <CavityLevel
                      testId="cavity-level-module"
                      unitData={lastUnitData}
                      edgeStatus={lastEdgeStatus}
                    />
                  </CavityLevelContainer>
                </Col>
              )}
            </Row>
            <Row
              style={{ flex: 1 }}
              gutter={{
                xs: 0,
                lg: 0,
                sm: 0,
                md: 0,
                xl: 8,
                xxl: 8,
              }}
            >
              <Col xl={12} lg={24} md={24} sm={24} xs={24}>
                <Lubrication
                  unitData={lastUnitData}
                  edgeStatus={lastEdgeStatus}
                  onOpenDrawer={(): void => {
                    setState({
                      ...state,
                      drawers: {
                        ...state.drawers,
                        isOpen: true,
                        selectedDrawer: PatriotDrawerType.lubrication,
                      },
                    });
                  }}
                  testId="lubrication-module"
                />
              </Col>

              <Col xl={12} lg={0} md={0} sm={0} xs={0}>
                <Camera
                  title={`${site?.name}`}
                  running={
                    lastEdgeStatus?.edgeStatus === EdgeDeviceOperationalStatusStatus.on
                  }
                  testId="camera-module"
                  unitCoordinates={unitCoordinates}
                  gpsEnabled={equipmentUnitCrusher.gpsEnabled}
                  isOpen={!isMobileOrTablet && state.cameraModal.isOpen}
                  onOpen={(isOpen: boolean): void => setState({
                    ...state,
                    cameraModal: { isOpen },
                  })}
                />
              </Col>
            </Row>
            {hasVibrationSensorEnabled && isMobileOrTablet && (
              <Row
                style={{ flex: 1 }}
                gutter={{
                  xs: 0,
                  lg: 0,
                  sm: 0,
                  md: 0,
                  xl: 8,
                  xxl: 8,
                }}
              >
                <Col>
                  <Vibration
                    testId="vibration-module"
                    unitData={lastUnitData}
                    edgeStatus={lastEdgeStatus}
                    onOpenDrawer={(): void => {
                      setState({
                        ...state,
                        drawers: {
                          ...state.drawers,
                          isOpen: true,
                          selectedDrawer: PatriotDrawerType.motorStatus,
                        },
                      });
                    }}
                  />
                </Col>
              </Row>
            )}
          </RightMetricsContainer>
        </Col>
      </Row>

      {state.trendings.isOpen && state.trendings.selectedTrending && (
        <Trendings
          testId="trendings"
          showTrendings={state.trendings.isOpen}
          operationalData={operationalData}
          onCloseTrendings={(): void => setState({
            ...state,
            trendings: { ...state.trendings, isOpen: false },
          })}
          alternativeTrending={
            state.trendings.selectedTrending === TrendingType.coastDown
              ? TrendingType.coastDown
              : undefined
          }
          idEquipmentUnit={equipmentUnitCrusher.id}
        />
      )}
      {
        state.maintenance.isOpen && state.maintenance.selectedView && (
          <Maintenance
            showView={state.maintenance.selectedView}
            unitData={lastUnitData}
            edgeStatus={lastEdgeStatus}
            onChangeView={handleMaintenanceViewChange}
            onClose={(): void => setState({
              ...state,
              maintenance: {
                ...state.maintenance,
                isOpen: false,
                selectedView: MaintenanceType.maintenance,
              },
            })}
          />
        )
      }

      {renderCrusherDrawer(state.drawers.selectedDrawer)}
      {isMobileOrTablet && state.cameraModal.isOpen && (
        <CameraFeed
          title={`${site?.name}`}
          isOpen={state.cameraModal.isOpen}
          live={
            lastEdgeStatus?.edgeStatus === EdgeDeviceOperationalStatusStatus.on
              ? LiveStatus.on
              : LiveStatus.off
          }
          testId="camera-feed-modal"
          unitCoordinates={unitCoordinates}
          gpsEnabled={equipmentUnitCrusher.gpsEnabled}
          onOpen={(isOpen: boolean): void => setState({
            ...state,
            cameraModal: { isOpen },
          })}
        />
      )}
    </CrusherMonitoringContainer>
  );
}

export { Patriot };
